import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { PerformanceDiagnosticsService } from '../services/performance-diagnostics/performance-diagnostics.service';

export const performanceDiagnosticsInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isSgiRequest(req.urlWithParams || req.url)) {
    return next(req);
  }

  const diagnostics = inject(PerformanceDiagnosticsService);
  const started = diagnostics.beginRequest(req.method, req.urlWithParams || req.url);
  const tracedReq = req.clone({
    setHeaders: {
      'X-SGI-Perf-Page-Scope-Id': started.scopeId,
    },
  });

  return next(tracedReq).pipe(
    tap(event => {
      if (event instanceof HttpResponse) {
        diagnostics.recordRequest({
          request_id: event.headers.get('X-SGI-Perf-Request-Id') || started.requestId,
          page_scope_id: event.headers.get('X-SGI-Perf-Page-Scope-Id') || started.scopeId,
          method: req.method,
          path: pathFromUrl(req.urlWithParams || req.url),
          url: req.urlWithParams || req.url,
          endpoint: event.headers.get('X-SGI-Perf-Endpoint') || 'angular-http-client',
          status_code: event.status,
          route_total_ms: elapsed(started.startedAt),
          client_total_ms: elapsed(started.startedAt),
          server_route_ms: numberHeader(event.headers.get('X-SGI-Perf-Route-Ms')),
          server_timing_header: event.headers.get('Server-Timing') || '',
          query_count: 0,
          query_ms_total: 0,
          response_bytes: responseBytes(event.body),
          captured_at: Date.now() / 1000,
          started_at: started.startedIso,
        });
      }
    }),
    catchError((err: unknown) => {
      const httpErr = err instanceof HttpErrorResponse ? err : null;
      diagnostics.recordRequest({
        request_id: started.requestId,
        page_scope_id: started.scopeId,
        method: req.method,
        path: pathFromUrl(req.urlWithParams || req.url),
        url: req.urlWithParams || req.url,
        endpoint: 'angular-http-client',
        status_code: httpErr?.status ?? 0,
        route_total_ms: elapsed(started.startedAt),
        client_total_ms: elapsed(started.startedAt),
        query_count: 0,
        query_ms_total: 0,
        captured_at: Date.now() / 1000,
        started_at: started.startedIso,
        error: httpErr?.message || 'Request failed',
      });
      return throwError(() => err);
    })
  );
};

function isSgiRequest(url: string): boolean {
  return url.startsWith(environment.SGI_API_URL);
}

function elapsed(startedAt: number): number {
  const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
  return Math.round((now - startedAt) * 100) / 100;
}

function pathFromUrl(url: string): string {
  try {
    return new URL(url, typeof window !== 'undefined' ? window.location.origin : undefined).pathname;
  } catch {
    return url;
  }
}

function numberHeader(value: string | null): number | undefined {
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function responseBytes(body: unknown): number | undefined {
  if (body == null) return 0;
  try {
    return new Blob([typeof body === 'string' ? body : JSON.stringify(body)]).size;
  } catch {
    return undefined;
  }
}
